import React from 'react'
import type { FormEvent } from 'react'
import { t } from '@lingui/core/macro'
import { Composer } from '@liveblocks/react-ui'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { RootState } from '@redux/store'
import { Button } from '@taxonomy-translation/components'
import CloseSVG from 'images/close.svg'
import VectorSVG from 'images/vector.svg'
import {
  getCommentText,
  getListValueText,
  formatRelativeTime,
  updateCellWithApprovedComment,
} from './helpers'
import type { HotTable } from '@handsontable/react'
import type { ThreadData } from '@liveblocks/client'
import type { QueuedResolutionStatus, List } from './types'

interface Props {
  activeCommentTarget: {
    id: string
    position: { top: number; left: number } | null
  } | null
  onClose: () => void
  list: List
  tableRef: React.RefObject<HotTable> | null
  threads: ThreadData[]
  updateLocalThreadStatus: (threadId: string, status: QueuedResolutionStatus) => void
  queueThreadResolution: (thread: ThreadData, status: QueuedResolutionStatus) => void
  createThread: ({ body, attachments, metadata }) => void
  isApproverMode: boolean
}

export const CommentDialog = ({
  activeCommentTarget,
  onClose,
  list,
  tableRef,
  threads,
  updateLocalThreadStatus,
  queueThreadResolution,
  createThread,
  isApproverMode,
}: Props) => {
  const { id: listValueId, position } = activeCommentTarget || {}
  const currentUser = useSelector((state: RootState) => state.Auth.user)

  const userId = currentUser?.uuid?.toString() || 'unknown id'
  const userName = currentUser?.name || 'unknown user'

  if (!listValueId || !position) return null

  const matchingThreads = threads
    .filter(thread => thread.metadata.listValueId === listValueId)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())

  const currentValue = getListValueText(list, listValueId)

  const handleComposerSubmit = ({ body, attachments }, e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    createThread?.({
      body,
      attachments,
      metadata: {
        listValueId,
        status: 'pending',
        userId,
        userName,
        prevValue: currentValue?.trim(),
        currentValue: currentValue?.trim(),
      },
    })
    onClose()
  }

  // find the thread with matching listValueId in metadata
  const handleStatusUpdate = (thread: ThreadData, status: QueuedResolutionStatus) => {
    // Update local thread status immediately
    updateLocalThreadStatus(thread.id, status)

    // Queue the resolution for when we save
    queueThreadResolution(thread, status)

    if (status === 'approved' && tableRef) {
      updateCellWithApprovedComment(thread, tableRef, list)
    }
    onClose()
  }

  const portalRoot = document.getElementById('comment-dialog-root')
  if (!portalRoot) {
    return null
  }

  const pendingThreads = matchingThreads.filter(thread => thread.metadata.status === 'pending')
  const lastThread = matchingThreads[matchingThreads.length - 1]
  const showComposer =
    !lastThread || (lastThread.resolved && lastThread.metadata.status !== 'pending')

  return createPortal(
    <div
      className="absolute z-[9999] w-[400px]"
      style={{ top: position.top, left: position.left }}
      onClick={onClose}
      role="presentation"
      onKeyDown={e => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <div
        role="presentation"
        onKeyDown={e => {
          if (e.key === 'Escape') {
            e.stopPropagation()
            onClose()
          }
        }}
        onClick={e => {
          e.stopPropagation()
        }}
      >
        {pendingThreads.map(thread => (
          <div
            key={thread.id}
            className="space-y-2 bg-white shadow rounded-[12px] p-5 mb-4 border border-gray-200 w-[240px]"
          >
            <div className="mb-4">
              <div className="max-h-[160px] overflow-y-auto">
                <p className="text-base text-[#0C1E23]">
                  <span className="font-medium">{t`Original:`}</span>{' '}
                  <span className="font-normal">{currentValue ?? '—'}</span>
                </p>
                <p className="text-base text-[#0C1E23] break-words whitespace-normal">
                  <span className="font-medium">{t`Suggestion:`}</span>{' '}
                  <span className="font-normal">{getCommentText(thread)}</span>
                </p>
              </div>
              <p className="text-sm mt-3">
                <span className="font-semibold text-[#30505A]">{`${thread.metadata.userName} `}</span>
                <span className="text-[#64748B]">
                  {formatRelativeTime(thread.metadata.updatedAt)}
                </span>
              </p>
            </div>
            {isApproverMode && (
              <div className="flex gap-3 mt-4">
                <Button
                  variant="success"
                  className="flex h-[28px] px-[10px] pt-[4px] pb-[6px]"
                  onClick={() => {
                    handleStatusUpdate(
                      {
                        ...thread,
                        metadata: {
                          ...thread.metadata,
                          moderatedById: userId,
                          moderatedByName: userName,
                        },
                      },
                      'approved',
                    )
                  }}
                >
                  <VectorSVG className="stroke-current hover:fill-white" />
                  <span>{t`Approve`}</span>
                </Button>
                <Button
                  variant="warn"
                  className="flex h-[28px] px-[10px] pt-[4px] pb-[6px]"
                  onClick={() => {
                    handleStatusUpdate(
                      {
                        ...thread,
                        metadata: {
                          ...thread.metadata,
                          moderatedById: userId,
                          moderatedByName: userName,
                        },
                      },
                      'rejected',
                    )
                  }}
                >
                  <CloseSVG className="stroke-current hover:fill-white" />
                  <span>{t`Reject`}</span>
                </Button>
              </div>
            )}
          </div>
        ))}

        {showComposer && (
          <Composer
            showAttachments={false}
            className="composer"
            onComposerSubmit={handleComposerSubmit}
            overrides={{
              COMPOSER_PLACEHOLDER: t`Suggest a new value for this cell`,
            }}
          />
        )}
      </div>
    </div>,
    portalRoot,
  )
}
